// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import Notify, { NotifyProps } from "@gcg/App/Components/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentCartState,
    hasQuoteRequiredProducts,
    hasRestrictedCartLines,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCurrentCartState(state).value;
    return {
        cart,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        hasRestrictedCartLines: hasRestrictedCartLines(cart),
        isCartPage: getCurrentPage(state)?.name === "Cart",
    };
};

export interface CartApproverMessageStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    notificationContainers?: NotifyProps;
}

export const cartApproverMessageStyles: CartApproverMessageStyles = {
    approvingWrapper: {
        css: css`
            width: calc(100% - 440px);
            text-align: center;
            margin-right: 40px;
            @media print {
                display: none;
            }
            @media (max-width: 1150px) {
                width: 100%;
                margin: 0;
            }
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
    notificationContainers: {
        notifyContainerOverrides: css`
            max-width: 100%;
            margin-bottom: 20px;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = cartApproverMessageStyles;
const CartApproverMessage = ({ cart, hasQuoteRequiredProducts, hasRestrictedCartLines, isCartPage }: Props) => {
    if (!cart || cart.cartLines?.length === 0) {
        return null;
    }

    const isHandling = Number(cart.properties?.handlingNumeric) > 0; // XNG GCG-290

    let cartNotPriced = false;
    let belowOrderThrethold = false;
    let inactiveProducts = false;
    let customQuote = false;
    let canModifyCuts = false;
    let cutsDoesNotEqualQty = false;
    const extendedCutTotal: boolean[] = [];

    if (cart && cart.cartLines?.length) {
        customQuote = cart.cartLines.some(line => line.pricing?.unitNetPrice === 0);
        inactiveProducts = cart.cartLines.some(line => !line.isActive);
        belowOrderThrethold = cart.properties["orderLessThanMinimumOrderValue"] === "true";
        cartNotPriced = cart?.cartNotPriced;
        canModifyCuts = cart?.cartLines.some(
            line =>
                line.properties["allowCutWire"] === "true" &&
                (line.properties["cutWire"] === undefined || line.properties["cutWire"] === ""),
        );
        cart?.cartLines.map(line => {
            if (
                line.properties["allowCutWire"] === "true" &&
                line.properties?.hasOwnProperty("cutWire") &&
                line.properties["cutWire"] !== ""
            ) {
                const cutObj = JSON.parse(line.properties["cutWire"]);
                const newExtendedTotal = cutObj.reduce(function (prev: any, current: any) {
                    return prev + parseInt(current.total, 10);
                }, 0);
                extendedCutTotal.push(line.qtyOrdered !== newExtendedTotal);
            }
        });
        cutsDoesNotEqualQty = extendedCutTotal.some(o => o);
    }

    const [outOfStock, setOutOfStock] = React.useState(false);
    if (cart && cart.cartLines) {
        const isOutStock = cart.cartLines?.some(line => line.availability!.messageType === 2);
        if (isOutStock !== outOfStock) {
            setOutOfStock(isOutStock);
        }
    }

    return (
        <StyledWrapper {...styles.approvingWrapper}>
            {cart.isAwaitingApproval && (
                <Notify
                    {...styles.notificationContainers}
                    showCloseButton={true}
                    messageType="info"
                    data-test-selector="cart_approvingOrderMessage"
                    body={translate("Approving Order for {0}", cart.initiatedByUserName?.toUpperCase())}
                />
            )}
            {(hasQuoteRequiredProducts || customQuote) && isCartPage && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Custom_Cart_has_quote_required_products")}
                />
            )}
            {hasRestrictedCartLines && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Cart_ProductsCannotBePurchased")}
                />
            )}
            {inactiveProducts && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Cart_ProductsCannotBePurchased")}
                />
            )}
            {belowOrderThrethold && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Cart_LessThanOrderThreshold")}
                />
            )}
            {cartNotPriced && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Cart_PriceNotAvailable")}
                />
            )}
            {cart.fulfillmentMethod.toLowerCase() === "pickup" && outOfStock && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Custom_Cart_out_of_stock_items")}
                />
            )}
            {canModifyCuts && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="info"
                    showCloseButton={true}
                    body={siteMessage("Custom_Cart_CanModifyCuts")}
                />
            )}
            {cutsDoesNotEqualQty && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="warning"
                    showCloseButton={true}
                    body={siteMessage("Custom_Cart_CutLengthsDoesNotMeetQtyOrdered")}
                />
            )}
            {isHandling && (
                <Notify
                    {...styles.notificationContainers}
                    messageType="info"
                    showCloseButton={true}
                    body={siteMessage("Custom_Cart_Handling_Fee")}
                />
            )}
        </StyledWrapper>
    );
};;;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartApproverMessage),
    definition: {
        displayName: "Cart Messaging",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
